/**
 * اشتقاق وسائط الهيرو لأوّل HTML (SSR) — لكلّ مقاس على حدة.
 *
 * السبب: الـloader كان يستدعي حملة واحدة بلا `device`، بينما العميل يختار
 * الحملة حسب المقاس. النتيجة: preload لأصل الموبايل بعرض ١٤٤٠ وصورة معروضة
 * من أصل الديسكتوب → تحميل أصل الجهاز الخطأ مرّتين.
 *
 * القاعدة هنا: كلّ مقاس يأخذ صورة حملته الخاصّة فقط (أو غلافها إن كان صورة).
 * ممنوع استعارة صورة المقاس الآخر — هي التي كانت تسبّب عدم التطابق.
 */

export type HeroCampaignMedia = {
  desktop?: string | null;
  mobile?: string | null;
  poster?: string | null;
} | null;

export type HeroLoaderData = {
  heroPoster: string | null;
  heroVideo: string | null;
  mobileImage: string | null;
  desktopImage: string | null;
};

export const EMPTY_HERO: HeroLoaderData = {
  heroPoster: null,
  heroVideo: null,
  mobileImage: null,
  desktopImage: null,
};

const isVideo = (url: string) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
const asImage = (url: string | null | undefined) =>
  url && !isVideo(url) ? url : null;
const asVideo = (url: string | null | undefined) =>
  url && isVideo(url) ? url : null;

/** صورة هذا المقاس: وسيطه إن كان صورة، وإلّا غلاف حملته إن كان صورة. */
function deviceImage(
  campaign: HeroCampaignMedia,
  key: "mobile" | "desktop",
): string | null {
  if (!campaign) return null;
  return asImage(campaign[key]) ?? asImage(campaign.poster);
}

export function deriveHeroLoaderData(
  mobileCampaign: HeroCampaignMedia,
  desktopCampaign: HeroCampaignMedia,
): HeroLoaderData {
  const mobileImage = deviceImage(mobileCampaign, "mobile");
  const desktopImage = deviceImage(desktopCampaign, "desktop");

  const mobileVideo = asVideo(mobileCampaign?.mobile);
  const desktopVideo = asVideo(desktopCampaign?.desktop);
  // preload الفيديو لا يقبل `media`، فهو ينزّل على كلّ المقاسات. لذلك لا نلمّح
  // إليه إلّا إذا كان المقاسان يشتركان في نفس الفيديو ولا صورة تسبقه.
  const sharedVideo =
    !mobileImage && !desktopImage && mobileVideo && mobileVideo === desktopVideo
      ? mobileVideo
      : null;

  return {
    heroPoster: desktopImage ?? mobileImage,
    heroVideo: sharedVideo,
    mobileImage,
    desktopImage,
  };
}
